import { z } from 'zod';
import { ASSIGNMENT_RULE_MAX_CHILDREN, ASSIGNMENT_RULE_MAX_DEPTH } from '@vagusx/shared';
import { hasEmptyNestedGroup, hasIncompleteLeaf, ruleNodeDepth } from '@vagusx/shared';
import type { AssignmentRuleNode } from '@vagusx/shared';

// =============================================================================
// Rule Kind
// =============================================================================

/**
 * Discriminator between rule variants. Stored in the `rule_kind` column.
 * Designed for forward-compat: future variants (after-hours, escalation,
 * etc.) extend this enum without bool-flag sprawl on the table.
 */
export const RULE_KIND = {
  REGULAR: 'regular',
  CATCH_ALL: 'catch_all',
} as const;
export type RuleKind = (typeof RULE_KIND)[keyof typeof RULE_KIND];

export const ruleKindSchema = z.enum(['regular', 'catch_all']);

/**
 * The catch-all assignment must resolve to a real human or human-staffed
 * team — no automated workers as the safety-net assignee. Single source of
 * truth shared by server validation, the editor's assignee picker, and the
 * card icon mapping.
 */
export const CATCH_ALL_ALLOWED_ASSIGNEES = ['user', 'team'] as const;
export type CatchAllAssignee = (typeof CATCH_ALL_ALLOWED_ASSIGNEES)[number];

// =============================================================================
// Condition Row Schema
// =============================================================================

export const assignmentRuleFieldSchema = z.enum([
  'priority',
  'insurance',
  'provider',
  'diagnoses',
  'services',
  'location',
  'label',
]);
export type AssignmentRuleFieldClient = z.infer<typeof assignmentRuleFieldSchema>;

export const conditionOperatorSchema = z.enum([
  'equals',
  'not_equals',
  'in',
  'not_in',
  'contains_any',
  'contains_all',
]);
export type ConditionOperatorClient = z.infer<typeof conditionOperatorSchema>;

export const logicalConnectorSchema = z.enum(['AND', 'OR']);

/**
 * @deprecated Used only by the data migration adapter. The on-disk
 * and over-the-wire shape is `ruleNodeSchema`. Kept exported so
 * legacy callers can read the shape during transition.
 */
export const conditionRowSchema = z.object({
  id: z.string(),
  connector: logicalConnectorSchema,
  field: assignmentRuleFieldSchema,
  operator: conditionOperatorSchema,
  value: z.union([z.string(), z.array(z.string())]),
});
export type ConditionRowClient = z.infer<typeof conditionRowSchema>;

// =============================================================================
// Rule Node Tree Schema (recursive)
// =============================================================================

/**
 * Recursive schema for the rule's boolean expression tree. Two
 * variants discriminated by `kind`:
 *  - `condition` — leaf, evaluated against the rule context
 *  - `group` — `op: 'all' | 'any'` over an array of children
 *
 * `z.lazy()` is required because the schema references itself
 * through the group's `children` array. The base case is the
 * `condition` variant; depth is enforced at the route level via the
 * `.refine()` below using `ruleNodeDepth`.
 */
export const ruleNodeSchema: z.ZodType<AssignmentRuleNode> = z.lazy(() =>
  z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('condition'),
      field: assignmentRuleFieldSchema,
      operator: conditionOperatorSchema,
      value: z.union([z.string(), z.array(z.string())]),
    }),
    z.object({
      kind: z.literal('group'),
      op: z.enum(['all', 'any']),
      children: z.array(ruleNodeSchema).max(ASSIGNMENT_RULE_MAX_CHILDREN),
    }),
  ])
);

/**
 * Schema with the depth limit enforced. The plain `ruleNodeSchema`
 * doesn't carry the depth check because Zod's `z.lazy` recursion
 * would re-invoke the refine on every level — instead, refine once
 * at the boundary (request payloads) using `ruleNodeDepth`.
 *
 * The empty-children case is permitted for the root group only —
 * an empty rule represents "no conditions yet" during edit. Save
 * payloads validate against this schema; the route layer requires
 * a non-empty root for `regular` rules and an empty root for
 * `catch_all`.
 */
export const ruleNodeRequestSchema = ruleNodeSchema
  .refine((node) => ruleNodeDepth(node) <= ASSIGNMENT_RULE_MAX_DEPTH, {
    message: `Rule nesting exceeds maximum depth of ${ASSIGNMENT_RULE_MAX_DEPTH}`,
  })
  // Nested empty groups make the rule silently dead (engine returns
  // false for empty groups → containing all-group is also false → rule
  // never matches). The root's own emptiness is allowed and policed by
  // the catch-all-vs-regular refine on the create/update payloads.
  .refine((node) => !hasEmptyNestedGroup(node), {
    message:
      'Nested groups must contain at least one condition — empty groups make the rule never match',
  })
  // Reject leaves missing field, operator, or value. A leaf with
  // `value: ''` (or `value: []`) types as valid but evaluates to false
  // at runtime — silently produces a dead rule. This is the
  // server-side gate; the editor's submit button is also blocked when
  // any leaf is incomplete.
  .refine((node) => !hasIncompleteLeaf(node), {
    message: 'Every condition needs a field, operator, and value',
  });

// =============================================================================
// Response Schema
// =============================================================================

export const taskAssignmentRuleResponseSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  priority: z.number(),
  conditions: ruleNodeSchema,
  assignToType: z.string(),
  assignToId: z.string(),
  assignToName: z.string().nullable().optional(),
  isEnabled: z.boolean(),
  // Variant discriminator. 'catch_all' is the org's single fallback
  // (at most one active per org); 'regular' is the priority-ordered
  // table. Reserved for future variants.
  ruleKind: ruleKindSchema,
  createdBy: z.string().nullable(),
  updatedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TaskAssignmentRuleResponse = z.infer<typeof taskAssignmentRuleResponseSchema>;

// =============================================================================
// Request Schemas
// =============================================================================

/**
 * Create payload. The catch-all constraints (conditions empty,
 * assignee user/team only, one per org) are enforced server-side
 * inside `TaskAssignmentRuleService.create` — the schema stays
 * permissive so the regular-rule path doesn't need to discriminate.
 */
export const createTaskAssignmentRuleSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(200),
    description: z.string().optional().nullable(),
    priority: z.number().int().optional(),
    conditions: ruleNodeRequestSchema,
    assignToType: z.enum(['user', 'agent', 'team']),
    assignToId: z.string().uuid('Valid assignment target is required'),
    isEnabled: z.boolean().optional(),
    ruleKind: ruleKindSchema.optional(),
  })
  // Regular rules require a non-empty root group; catch-alls require
  // an empty root. The path-level error lands on `conditions` for the
  // user. Empty group = `kind: 'group', children: []`.
  .refine(
    (data) => {
      const isEmpty = data.conditions.kind === 'group' && data.conditions.children.length === 0;
      if (data.ruleKind === 'catch_all') return isEmpty;
      return !isEmpty;
    },
    {
      message: 'At least one condition is required (or set ruleKind=catch_all for the fallback)',
      path: ['conditions'],
    }
  );
export type CreateTaskAssignmentRuleRequest = z.infer<typeof createTaskAssignmentRuleSchema>;

export const updateTaskAssignmentRuleSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().optional().nullable(),
  priority: z.number().int().optional(),
  conditions: ruleNodeRequestSchema.optional(),
  assignToType: z.enum(['user', 'agent', 'team']).optional(),
  assignToId: z.string().uuid().optional(),
  isEnabled: z.boolean().optional(),
  // ruleKind is intentionally absent — variant is set at create time
  // only. To convert a regular rule, delete and re-create.
});
export type UpdateTaskAssignmentRuleRequest = z.infer<typeof updateTaskAssignmentRuleSchema>;

export const reorderAssignmentRulesSchema = z.object({
  ruleIds: z.array(z.string().uuid()).min(1),
});
export type ReorderAssignmentRulesRequest = z.infer<typeof reorderAssignmentRulesSchema>;

/**
 * Which step of assignment produced the match (ADR-352): a sub-task's parent,
 * the org's own rules, the inbound-default phone team for an inbound call
 * follow-up, or the catch-all.
 */
export const assignmentSourceSchema = z.enum([
  'parent_task',
  'rule',
  'inbound_default_team',
  'catch_all',
]);
export type AssignmentSource = z.infer<typeof assignmentSourceSchema>;

/** Dry-run evaluation of a task — every field null when nothing applies. */
export const assignmentEvaluationSchema = z.object({
  source: assignmentSourceSchema.nullable(),
  ruleId: z.string().uuid().nullable(),
  assignToType: z.string().nullable(),
  assignToId: z.string().uuid().nullable(),
});
export type AssignmentEvaluation = z.infer<typeof assignmentEvaluationSchema>;
